import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import EventCard from '../components/events/EventCard';
import EventSubmissionForm from '../components/events/EventSubmissionForm';
import type { EventDetail, EventSubmission, EventSubmissionRequest, EventSummary } from '../events/types';
import { api, logHttpError } from '../services/api';

type EventTab = 'active' | 'ended';
type RetryAction =
  | { kind: 'json'; request: EventSubmissionRequest }
  | { kind: 'file'; file: File };

const uniqueEvents = (items: EventSummary[]) => Array.from(new Map(items.map((event) => [event.id, event])).values());

const errorMessage = (error: unknown, fallback: string) => {
  const detail = typeof error === 'object' && error !== null && 'response' in error
    ? (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
    : undefined;
  return typeof detail === 'string' ? detail : fallback;
};

const EventsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedId = Number(new URLSearchParams(location.search).get('event')) || null;
  const [tab, setTab] = useState<EventTab>('active');
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [joiningId, setJoiningId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submission, setSubmission] = useState<EventSubmission | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [retryAction, setRetryAction] = useState<RetryAction | null>(null);
  const mountedRef = useRef(false);
  const tabRef = useRef<EventTab>(tab);
  const selectedEventIdRef = useRef<number | null>(selectedId);
  const listRequestIdRef = useRef(0);
  const detailRequestIdRef = useRef(0);
  const actionRequestIdRef = useRef(0);
  const joinRequestIdRef = useRef(0);

  tabRef.current = tab;
  selectedEventIdRef.current = selectedId;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      listRequestIdRef.current += 1;
      detailRequestIdRef.current += 1;
      actionRequestIdRef.current += 1;
      joinRequestIdRef.current += 1;
    };
  }, []);

  const loadEvents = useCallback(async () => {
    const requestedTab = tab;
    const requestId = listRequestIdRef.current + 1;
    listRequestIdRef.current = requestId;
    const requestIsCurrent = () => mountedRef.current
      && tabRef.current === requestedTab
      && listRequestIdRef.current === requestId;

    if (!requestIsCurrent()) return;
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<EventSummary[]>(`/events?state=${requestedTab}&limit=50`);
      if (!requestIsCurrent()) return;
      setEvents(uniqueEvents(response.data));
    } catch (requestError) {
      if (!requestIsCurrent()) return;
      logHttpError('Ошибка загрузки событий', requestError);
      setError(errorMessage(requestError, 'Не удалось загрузить события'));
    } finally {
      if (requestIsCurrent()) setLoading(false);
    }
  }, [tab]);

  const refreshEvent = useCallback(async (eventId: number, hydrateSubmission = true) => {
    const requestId = detailRequestIdRef.current + 1;
    detailRequestIdRef.current = requestId;
    const requestIsCurrent = () => mountedRef.current
      && selectedEventIdRef.current === eventId
      && detailRequestIdRef.current === requestId;

    if (!requestIsCurrent()) return;
    try {
      setDetailLoading(true);
      setDetailError(null);
      const response = await api.get<EventDetail>(`/events/${eventId}`);
      if (!requestIsCurrent()) return;
      setSelectedEvent(response.data);
      if (hydrateSubmission) setSubmission(response.data.submission);
      setEvents((current) => current.map((event) => event.id === eventId ? response.data : event));
    } catch (requestError) {
      if (!requestIsCurrent()) return;
      logHttpError('Ошибка загрузки события', requestError);
      setDetailError(errorMessage(requestError, 'Не удалось загрузить событие'));
    } finally {
      if (requestIsCurrent()) setDetailLoading(false);
    }
  }, []);

  const refreshListEvent = useCallback(async (eventId: number) => {
    try {
      const response = await api.get<EventDetail>(`/events/${eventId}`);
      if (!mountedRef.current) return;
      setEvents((current) => current.map((event) => event.id === eventId ? response.data : event));
    } catch (requestError) {
      if (!mountedRef.current || selectedEventIdRef.current !== eventId) return;
      logHttpError('Ошибка обновления события', requestError);
      setDetailError(errorMessage(requestError, 'Не удалось обновить событие'));
    }
  }, []);

  useEffect(() => { void loadEvents(); }, [loadEvents]);
  useEffect(() => {
    actionRequestIdRef.current += 1;
    detailRequestIdRef.current += 1;
    setSelectedEvent(null);
    setDetailLoading(Boolean(selectedId));
    setDetailError(null);
    setSubmission(null);
    setSubmissionError(null);
    setRetryAction(null);
    setJoiningId(null);
    setSubmitting(false);
    if (selectedId) void refreshEvent(selectedId);
  }, [refreshEvent, selectedId]);

  const openEvent = (eventId: number) => navigate(`/events?event=${eventId}`);

  const joinEvent = async (eventId: number) => {
    const requestId = joinRequestIdRef.current + 1;
    joinRequestIdRef.current = requestId;
    const requestIsCurrent = () => mountedRef.current && joinRequestIdRef.current === requestId;

    if (!requestIsCurrent()) return;
    try {
      setJoiningId(eventId);
      if (selectedEventIdRef.current === eventId) setDetailError(null);
      await api.post(`/events/${eventId}/join`);
      if (!requestIsCurrent()) return;
      if (selectedEventIdRef.current === eventId) await refreshEvent(eventId);
      else await refreshListEvent(eventId);
    } catch (requestError) {
      if (!requestIsCurrent()) return;
      logHttpError('Ошибка присоединения к событию', requestError);
      if (selectedEventIdRef.current === eventId) setDetailError(errorMessage(requestError, 'Не удалось присоединиться к событию'));
    } finally {
      if (requestIsCurrent()) setJoiningId(null);
    }
  };

  const sendSubmission = async (request: EventSubmissionRequest, isRetry = false) => {
    if (!selectedEvent) return;
    const eventId = selectedEvent.id;
    const requestId = actionRequestIdRef.current + 1;
    actionRequestIdRef.current = requestId;
    const requestIsCurrent = () => mountedRef.current
      && selectedEventIdRef.current === eventId
      && actionRequestIdRef.current === requestId;

    if (!requestIsCurrent()) return;
    if (!isRetry) setRetryAction({ kind: 'json', request });
    try {
      setSubmitting(true);
      setSubmissionError(null);
      const response = await api.put<EventSubmission>(`/events/${eventId}/submission`, request);
      if (!requestIsCurrent()) return;
      setSubmission(response.data);
      setRetryAction(null);
      await refreshEvent(eventId, false);
    } catch (requestError) {
      if (!requestIsCurrent()) return;
      logHttpError('Ошибка отправки задания', requestError);
      setSubmissionError(errorMessage(requestError, 'Не удалось отправить задание'));
    } finally {
      if (requestIsCurrent()) setSubmitting(false);
    }
  };

  const uploadSubmission = async (file: File, isRetry = false) => {
    if (!selectedEvent) return;
    const eventId = selectedEvent.id;
    const requestId = actionRequestIdRef.current + 1;
    actionRequestIdRef.current = requestId;
    const requestIsCurrent = () => mountedRef.current
      && selectedEventIdRef.current === eventId
      && actionRequestIdRef.current === requestId;

    if (!requestIsCurrent()) return;
    if (!isRetry) setRetryAction({ kind: 'file', file });
    try {
      setSubmitting(true);
      setSubmissionError(null);
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<EventSubmission>(`/events/${eventId}/submission/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!requestIsCurrent()) return;
      setSubmission(response.data);
      setRetryAction(null);
      await refreshEvent(eventId, false);
    } catch (requestError) {
      if (!requestIsCurrent()) return;
      logHttpError('Ошибка загрузки задания', requestError);
      setSubmissionError(errorMessage(requestError, 'Не удалось загрузить задание'));
    } finally {
      if (requestIsCurrent()) setSubmitting(false);
    }
  };

  const retrySubmission = () => {
    if (retryAction?.kind === 'json') void sendSubmission(retryAction.request, true);
    if (retryAction?.kind === 'file') void uploadSubmission(retryAction.file, true);
  };

  const submissionStateLabel: Record<EventSubmission['state'], string> = {
    draft: 'Черновик',
    submitted: 'На проверке',
    accepted: 'Принято',
    rejected: 'Отклонено',
  };

  return <div className="min-h-screen bg-[#0f1115] pb-20 text-white"><main className="mx-auto max-w-5xl p-4 md:p-6"><div className="mb-6 flex flex-wrap items-center justify-between gap-3"><div><h1 className="text-3xl font-black">События</h1><p className="shark-muted mt-1">Участвуйте в заданиях сообщества и получайте награды.</p></div></div><div role="tablist" aria-label="Состояние событий" className="shark-card shark-border mb-6 flex rounded-2xl border p-1"><button type="button" role="tab" aria-selected={tab === 'active'} onClick={() => setTab('active')} className={`shark-density-control flex-1 rounded-xl text-sm font-bold ${tab === 'active' ? 'shark-accent-button' : 'shark-muted hover:bg-gray-800'}`}>Активные</button><button type="button" role="tab" aria-selected={tab === 'ended'} onClick={() => setTab('ended')} className={`shark-density-control flex-1 rounded-xl text-sm font-bold ${tab === 'ended' ? 'shark-accent-button' : 'shark-muted hover:bg-gray-800'}`}>Завершённые</button></div><div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)]"><section aria-label="Список событий">{loading ? <div className="shark-card shark-border rounded-2xl border p-8 text-center"><div className="mx-auto mb-3 h-7 w-7 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" /><p className="shark-muted">Загружаем события...</p></div> : error ? <div className="shark-card shark-border rounded-2xl border p-6 text-center"><p className="shark-error font-bold">{error}</p><button type="button" onClick={loadEvents} className="shark-accent-button shark-density-control mt-4 rounded-xl font-bold">Повторить загрузку событий</button></div> : events.length === 0 ? <div className="shark-card shark-border rounded-2xl border p-8 text-center"><p className="shark-muted">Событий в этой вкладке пока нет.</p></div> : <div className="grid gap-4 sm:grid-cols-2">{events.map((event) => <EventCard key={event.id} event={event} onOpen={openEvent} onJoin={joinEvent} joining={joiningId === event.id} />)}</div>}</section><aside aria-live="polite">{detailLoading ? <div className="shark-card shark-border rounded-2xl border p-6"><p className="shark-muted">Загружаем детали события...</p></div> : detailError ? <div className="shark-card shark-border rounded-2xl border p-6"><p className="shark-error">{detailError}</p>{selectedId && <button type="button" onClick={() => refreshEvent(selectedId)} className="shark-accent-button shark-density-control mt-3 rounded-xl font-bold">Повторить загрузку события</button>}</div> : selectedEvent ? <div className="shark-card shark-border rounded-2xl border"><h2 className="text-2xl font-bold text-white">{selectedEvent.title}</h2><p className="shark-muted mt-3 whitespace-pre-wrap text-sm">{selectedEvent.instructions}</p>{selectedEvent.can_join && <button type="button" onClick={() => joinEvent(selectedEvent.id)} disabled={joiningId === selectedEvent.id} aria-label={`Присоединиться к событию ${selectedEvent.title}`} className="shark-accent-button shark-density-control mt-5 rounded-xl font-bold disabled:opacity-50">Присоединиться</button>}<EventSubmissionForm event={selectedEvent} submitting={submitting} onSubmitText={(text) => { void sendSubmission({ text_value: text }); }} onSubmitConfirmation={() => { void sendSubmission({ confirmed: true }); }} onUpload={(file) => { void uploadSubmission(file); }} />{submission && <p className="mt-4 text-sm font-bold text-white">Статус отправки: {submissionStateLabel[submission.state]}</p>}{submissionError && <div className="mt-3"><p className="shark-error text-sm">{submissionError}</p>{retryAction && <button type="button" onClick={retrySubmission} disabled={submitting} className="shark-accent-button shark-density-control mt-2 rounded-xl text-sm font-bold disabled:opacity-50">Повторить отправку задания</button>}</div>}{submission?.review_note && <div className="mt-4 rounded-xl border border-gray-700 p-3"><h3 className="text-sm font-bold">Заметка модератора</h3><p className="shark-muted mt-1 text-sm">{submission.review_note}</p></div>}</div> : <div className="shark-card shark-border rounded-2xl border p-6"><p className="shark-muted">Выберите событие, чтобы увидеть условия и отправить задание.</p></div>}</aside></div></main></div>;
};

export default EventsPage;
